import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import Login from './Login';
import AppOnboarding from './components/AppOnboarding'; // New Import

import './modal-fix.css';
import './mobile-fix.css';

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [newProduct, setNewProduct] = useState({
    name: '',
    category: '',
    price: '',
    stock: '',
    description: '',
    status: 'pending'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editingProduct, setEditingProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [draggedProduct, setDraggedProduct] = useState(null);
  const [touchStartY, setTouchStartY] = useState(0); // eslint-disable-line no-unused-vars
  const [sales, setSales] = useState([]); // eslint-disable-line no-unused-vars
  const [sellQuantity, setSellQuantity] = useState({});
  const [showSalesHistory, setShowSalesHistory] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [monthlySales, setMonthlySales] = useState({});
  const [expandedMonths, setExpandedMonths] = useState(new Set([new Date().toISOString().slice(0, 7)]));
  const [lastCheckedMonth, setLastCheckedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [showPendingDetails, setShowPendingDetails] = useState(false);
  const [selectedPendingProduct, setSelectedPendingProduct] = useState(null);

  // MOBILE TOUCH STATES
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartTime, setDragStartTime] = useState(0);

  // BULLETPROOF PAGINATION & PERFORMANCE
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const ITEMS_PER_PAGE = 50;
  const [hasMore, setHasMore] = useState(true);
  const [cachedProducts, setCachedProducts] = useState(new Map());
  const [lastFetch, setLastFetch] = useState(0);
  const CACHE_DURATION = 30000; // 30 seconds

  // Custom Toast Notification System - FIXED
  const showToast = (message, type = 'success') => {
    // Prevent duplicate toasts with same message
    const sanitizedMessage = String(message)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');

    // Check if same message already exists
    setToasts(prev => {
      const exists = prev.some(t => t.message === sanitizedMessage);
      if (exists) return prev; // Don't add duplicate

      const id = Date.now() + Math.random(); // More unique ID
      const toast = { id, message: sanitizedMessage, type };

      // Auto remove after 3 seconds
      setTimeout(() => {
        setToasts(current => current.filter(t => t.id !== id));
      }, 3000);

      return [...prev, toast];
    });
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Show detailed view for pending products
  const showPendingProductDetails = (product) => {
    setSelectedPendingProduct(product);
    setShowPendingDetails(true);
  };

  // Undo/Delete sell functionality
  const undoSale = async (saleId) => {
    try {
      // Get the sale details first
      const { data: saleData, error: fetchError } = await supabase
        .from('sales')
        .select('*')
        .eq('id', saleId)
        .single();

      if (fetchError) {
        showToast('Error fetching sale details', 'error');
        return;
      }

      // Delete the sale record
      const { error: deleteError } = await supabase
        .from('sales')
        .delete()
        .eq('id', saleId);

      if (deleteError) {
        showToast('Error deleting sale: ' + deleteError.message, 'error');
        return;
      }

      // Restore stock to the product
      const { error: updateError } = await supabase
        .from('products')
        .update({
          stock: saleData.quantity + saleData.stock_after_sale
        })
        .eq('id', saleData.product_id);

      if (updateError) {
        showToast('Error restoring stock: ' + updateError.message, 'error');
        return;
      }

      showToast('Sale undone successfully - Stock restored', 'success');

      // Refresh data
      fetchSales();
      fetchProducts(currentPage, searchTerm, selectedCategory, true);

    } catch (error) {
      console.error('Error undoing sale:', error);
      showToast('Error undoing sale: ' + error.message, 'error');
    }
  };

  // BULLETPROOF IMMEDIATE DATABASE FETCHING
  const fetchProducts = async (page = 1, search = '', category = 'all', forceRefresh = false, showLoading = false) => {
    if (!user) return;

    const cacheKey = `${page}-${search}-${category}`;
    const now = Date.now();

    // Skip cache for immediate updates when forceRefresh is true
    if (!forceRefresh && cachedProducts.has(cacheKey) && (now - lastFetch) < CACHE_DURATION) {
      const cached = cachedProducts.get(cacheKey);
      setProducts(cached.data);
      setTotalProducts(cached.total);
      setHasMore(cached.hasMore);
      return;
    }

    try {
      // Only show loading when explicitly requested
      if (showLoading) {
        setSearchLoading(true);
      }

      // IMMEDIATE DATABASE QUERY - No delays
      let query = supabase
        .from('products')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE - 1);

      // Add search filter
      if (search.trim()) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      // Add category filter
      if (category !== 'all') {
        query = query.eq('category', category);
      }

      // EXECUTE QUERY IMMEDIATELY
      const { data, error, count } = await query;

      if (error) {
        console.error('Products fetch error:', error);
        showToast('Error loading products', 'error');
        return;
      }

      const hasMoreData = count > page * ITEMS_PER_PAGE;

      // Update cache immediately
      const cacheData = {
        data: data || [],
        total: count || 0,
        hasMore: hasMoreData,
        timestamp: now
      };

      setCachedProducts(prev => new Map(prev.set(cacheKey, cacheData)));
      setLastFetch(now);

      // UPDATE UI IMMEDIATELY
      setProducts(data || []);
      setTotalProducts(count || 0);
      setHasMore(hasMoreData);

      console.log(`⚡ INSTANT DB FETCH - Page ${page}: ${data?.length || 0} products (Total: ${count})`);

    } catch (error) {
      console.error('Products fetch exception:', error);
      showToast('Network error loading products', 'error');
      setProducts([]);
    } finally {
      if (showLoading) {
        setSearchLoading(false);
      }
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!user) return;

    try {
      // IMMEDIATE DATABASE QUERY
      const { data, error } = await supabase
        .from('categories')
        .select('name')
        .eq('user_id', user.id)
        .order('name');

      if (error) {
        console.error('Categories fetch error:', error);
        showToast('Error loading categories', 'error');
        return;
      }

      // UPDATE UI IMMEDIATELY
      setCategories(data?.map(cat => cat.name) || []);
      console.log('⚡ INSTANT Categories DB FETCH:', data?.length || 0, 'categories');
    } catch (error) {
      console.error('Categories fetch exception:', error);
      showToast('Network error loading categories', 'error');
      setCategories([]);
    }
  };

  const fetchSales = async () => {
    if (!user) return;

    try {
      // IMMEDIATE DATABASE QUERY
      const { data, error } = await supabase
        .from('sales')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Sales fetch error:', error);
        showToast('Error loading sales', 'error');
        return;
      }

      // UPDATE UI IMMEDIATELY
      setSales(data || []);

      // Group sales by month IMMEDIATELY
      const grouped = {};
      (data || []).forEach(sale => {
        const monthKey = new Date(sale.created_at).toISOString().slice(0, 7);
        if (!grouped[monthKey]) {
          grouped[monthKey] = [];
        }
        grouped[monthKey].push(sale);
      });
      setMonthlySales(grouped);

      console.log('⚡ INSTANT Sales DB FETCH:', data?.length || 0, 'sales');

    } catch (error) {
      console.error('Sales fetch exception:', error);
      showToast('Network error loading sales', 'error');
      setSales([]);
      setMonthlySales({});
    }
  };

  // AUTHENTICATION SETUP
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Utility function for getting current month
  const getCurrentMonth = () => {
    return new Date().toISOString().slice(0, 7);
  };

  // Auto-switch months when new month starts
  useEffect(() => {
    const currentMonth = getCurrentMonth();
    if (currentMonth !== lastCheckedMonth) {
      // New month started - close previous month and open current month
      setExpandedMonths(new Set([currentMonth]));
      setLastCheckedMonth(currentMonth);
      console.log(`📅 New month started: ${currentMonth}`);
    }
  }, [lastCheckedMonth]);

  // Check for month change every minute
  useEffect(() => {
    const checkMonthChange = () => {
      const currentMonth = getCurrentMonth();
      if (currentMonth !== lastCheckedMonth) {
        setExpandedMonths(new Set([currentMonth]));
        setLastCheckedMonth(currentMonth);
        console.log(`📅 Month changed to: ${currentMonth}`);
      }
    };

    const interval = setInterval(checkMonthChange, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [lastCheckedMonth]);

  // DATA LOADING WHEN USER CHANGES
  useEffect(() => {
    if (user) {
      setCurrentPage(1);
      setCachedProducts(new Map());
      fetchProducts(1, searchTerm, selectedCategory, true);
      fetchCategories();
      fetchSales();
    } else {
      setProducts([]);
      setCategories([]);
      setSales([]);
      setCurrentPage(1);
      setTotalProducts(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only depend on user

  // INSTANT SEARCH WITH IMMEDIATE DATABASE QUERIES
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (user) {
        setCurrentPage(1);
        setCachedProducts(new Map()); // Clear cache on search
        // IMMEDIATE DATABASE SEARCH - show loading only for search
        fetchProducts(1, searchTerm, selectedCategory, true, searchTerm.length > 0);
      }
    }, 300); // Reduced to 300ms for faster response

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedCategory, user]);

  // 🎯 VIEWPORT CENTERING - Ensure modals appear in center of user's current view
  useEffect(() => {
    const ensureViewportCentering = () => {
      const modals = document.querySelectorAll('[class*="fixed inset-0"]');
      modals.forEach(modal => {
        // Force viewport centering
        modal.style.position = 'fixed';
        modal.style.top = '0';
        modal.style.left = '0';
        modal.style.right = '0';
        modal.style.bottom = '0';
        modal.style.width = '100vw';
        modal.style.height = '100vh';
        modal.style.display = 'flex';
        modal.style.alignItems = 'center';
        modal.style.justifyContent = 'center';
        modal.style.zIndex = '9999';
        modal.style.margin = '0';
        modal.style.padding = '1rem';
      });
    };

    // Run when any modal state changes
    if (showModal || showCategoryModal || showSalesHistory || showPendingDetails) {
      setTimeout(ensureViewportCentering, 0);
    }
  }, [showModal, showCategoryModal, showSalesHistory, showPendingDetails]);

  // 🚀 OPTIMIZED SUPABASE REAL-TIME SYNC - INSTANT UPDATES ACROSS ALL USERS
  useEffect(() => {
    if (!user) return;

    console.log('🚀 Setting up OPTIMIZED Supabase real-time for user:', user.id);

    // Debounce mechanism to prevent multiple rapid triggers
    let debounceTimeout = null;
    const debouncedRefresh = (type) => {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        console.log(`🔄 Debounced refresh triggered for: ${type}`);
        setCachedProducts(new Map());
        fetchProducts(currentPage, searchTerm, selectedCategory, true);
        fetchCategories();
        fetchSales();
      }, 500); // 500ms debounce
    };

    // Create optimized real-time channel with instant updates
    const channel = supabase
      .channel(`instant_sync_${user.id}`, {
        config: {
          broadcast: { self: false }, // Don't receive own changes
          presence: { key: user.id },
          realtime: {
            heartbeat_interval: 5000, // Faster heartbeat
            reconnect_after: 1000, // Quicker reconnection
          }
        }
      })
      // Products - INSTANT updates
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'products',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        console.log('⚡ INSTANT Product change:', payload.eventType, payload.new || payload.old);

        // Use debounced refresh to prevent multiple rapid updates
        debouncedRefresh('products');

        // Show instant notification
        if (payload.eventType === 'INSERT') {
          showToast(`📦 New product added: ${payload.new?.name}`, 'success');
        } else if (payload.eventType === 'UPDATE') {
          showToast(`✏️ Product updated: ${payload.new?.name}`, 'info');
        } else if (payload.eventType === 'DELETE') {
          showToast(`🗑️ Product deleted`, 'warning');
        }
      })
      // Categories - INSTANT updates
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'categories',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        console.log('⚡ INSTANT Category change:', payload.eventType, payload.new || payload.old);

        // Use debounced refresh to prevent multiple rapid updates
        debouncedRefresh('categories');

        if (payload.eventType === 'INSERT') {
          const categoryName = payload.new?.name || 'Unknown Category';
          showToast(`📂 New category: ${categoryName}`, 'success');
        } else if (payload.eventType === 'DELETE') {
          showToast(`🗑️ Category deleted`, 'warning');
        }
      })
      // Sales - INSTANT updates
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'sales',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        console.log('⚡ INSTANT Sales change:', payload.eventType, payload.new || payload.old);

        // Use debounced refresh to prevent multiple rapid updates
        debouncedRefresh('sales');

        if (payload.eventType === 'INSERT') {
          const amount = parseFloat(payload.new?.total_amount || 0);
          showToast(`💰 Sale recorded: ₹${amount.toLocaleString('en-IN')}`, 'success');
        }
      })
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ INSTANT Real-time subscribed successfully');
          showToast('🚀 Real-time sync active - Instant updates enabled!', 'success');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Real-time error:', err);
          showToast('❌ Real-time connection failed', 'error');
        } else if (status === 'TIMED_OUT') {
          console.error('❌ Real-time timeout');
          showToast('⏰ Real-time connection timeout', 'warning');
        }
      });

    // INSTANT REFRESH ON WINDOW FOCUS - NO DELAYS
    const handleFocus = () => {
      console.log('⚡ WINDOW FOCUS - Immediate DB refresh');
      setCachedProducts(new Map()); // Clear cache for immediate refresh
      fetchProducts(currentPage, searchTerm, selectedCategory, true);
      fetchCategories();
      fetchSales();
    };
    window.addEventListener('focus', handleFocus);
    window.addEventListener('visibilitychange', handleFocus);

    // NETWORK RECONNECTION HANDLER - IMMEDIATE SYNC
    const handleOnline = () => {
      console.log('🌐 NETWORK RECONNECTED - IMMEDIATE FULL DB SYNC');
      setCachedProducts(new Map());
      // Force immediate refresh of all data
      fetchProducts(currentPage, searchTerm, selectedCategory, true);
      fetchCategories();
      fetchSales();
    };
    window.addEventListener('online', handleOnline);

    // Enhanced visibility change handler for better cross-device sync
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('⚡ Tab became visible - Force sync all data');
        setCachedProducts(new Map());
        fetchProducts(currentPage, searchTerm, selectedCategory, true);
        fetchCategories();
        fetchSales();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      console.log('🧹 Cleaning up real-time subscriptions');
      if (debounceTimeout) clearTimeout(debounceTimeout);
      supabase.removeChannel(channel);

      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('visibilitychange', handleFocus);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentPage, searchTerm, selectedCategory, lastFetch]); // Fixed dependencies

  const handleLogin = () => {
    // Handled by auth state change
  };

  const handleLogout = async () => {
    try {
      console.log('Starting logout process...');
      setLoading(true);

      // Try to sign out from Supabase
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('Supabase logout error:', error);
        // Even if there's an error, clear local state
        clearLocalState();
        showToast('Logged out (with warning): ' + error.message, 'error');
      } else {
        console.log('Supabase logout successful');
        clearLocalState();
        showToast('Logged out successfully', 'success');
      }
    } catch (error) {
      console.error('Logout exception:', error);
      // Force logout by clearing local state
      clearLocalState();
      showToast('Force logged out due to error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearLocalState = () => {
    console.log('Clearing local state...');
    setUser(null);
    setProducts([]);
    setCategories([]);
    setSales([]);
    setMonthlySales({});
    setCachedProducts(new Map());
    setCurrentPage(1);
    setTotalProducts(0);
    setSearchTerm('');
    setSelectedCategory('all');
    setEditingProduct(null);
    setShowModal(false);
    setShowCategoryModal(false);
    setShowSalesHistory(false);
    setSellQuantity({});
    console.log('Local state cleared');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 gradient-smooth flex items-center justify-center">
        <div className="text-center smooth-all">
          <div className="loading-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4 gpu-accelerated"></div>
          <p className="text-gray-600 smooth-opacity">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  // BULLETPROOF CRUD OPERATIONS
  const addProduct = async () => {
    // Enhanced input validation
    if (!newProduct.name?.trim() || !newProduct.category || !newProduct.price || !newProduct.stock) {
      showToast('Please fill all required fields', 'error');
      return;
    }

    const price = parseFloat(newProduct.price);
    const stock = parseInt(newProduct.stock);

    // Validate data types and ranges
    if (isNaN(price) || price <= 0) {
      showToast('Price must be a positive number', 'error');
      return;
    }

    if (isNaN(stock) || stock < 0) {
      showToast('Stock must be a non-negative number', 'error');
      return;
    }

    if (newProduct.name.trim().length > 100) {
      showToast('Product name must be less than 100 characters', 'error');
      return;
    }

    if (newProduct.description && newProduct.description.length > 500) {
      showToast('Description must be less than 500 characters', 'error');
      return;
    }

    try {
      const product = {
        name: newProduct.name.trim(),
        category: newProduct.category,
        price: price,
        stock: stock,
        description: newProduct.description?.trim() || '',
        status: 'pending',
        user_id: user.id
      };

      console.log('Adding product with status:', product.status);

      // DATABASE FIRST - NO OPTIMISTIC UPDATES
      const { data, error } = await supabase.from('products').insert([product]).select();

      console.log('Product saved to DB:', data);

      if (error) {
        showToast('Error adding product: ' + error.message, 'error');
        return;
      }

      // VERIFY DATA WAS SAVED
      const { data: verify } = await supabase.from('products').select('*').eq('id', data[0].id).single();
      if (!verify) {
        showToast('Data verification failed - please try again', 'error');
        return;
      }

      setNewProduct({ name: '', category: '', price: '', stock: '', description: '', status: 'pending' });
      setShowModal(false);
      showToast(`Added ${product.name}`, 'success');

      // FORCE REFRESH TO ENSURE SYNC
      setCachedProducts(new Map());
      fetchProducts(currentPage, searchTerm, selectedCategory, true);

    } catch (error) {
      console.error('Error adding product:', error);
      showToast('Error adding product: ' + error.message, 'error');
    }
  };

  const updateProduct = async () => {
    // Enhanced input validation
    if (!editingProduct.name?.trim() || !editingProduct.category || !editingProduct.price || !editingProduct.stock) {
      showToast('Please fill all required fields', 'error');
      return;
    }

    const price = parseFloat(editingProduct.price);
    const stock = parseInt(editingProduct.stock);

    // Validate data types and ranges
    if (isNaN(price) || price <= 0) {
      showToast('Price must be a positive number', 'error');
      return;
    }

    if (isNaN(stock) || stock < 0) {
      showToast('Stock must be a non-negative number', 'error');
      return;
    }

    if (editingProduct.name.trim().length > 100) {
      showToast('Product name must be less than 100 characters', 'error');
      return;
    }

    if (editingProduct.description && editingProduct.description.length > 500) {
      showToast('Description must be less than 500 characters', 'error');
      return;
    }

    try {

      // DATABASE FIRST - NO OPTIMISTIC UPDATES
      const { error } = await supabase
        .from('products')
        .update({
          name: editingProduct.name.trim(),
          category: editingProduct.category,
          price: price,
          stock: stock,
          description: editingProduct.description?.trim() || '',
          updated_at: new Date().toISOString()
        })
        .eq('id', editingProduct.id)
        .eq('user_id', user.id);

      if (error) {
        showToast('Error updating product: ' + error.message, 'error');
        return;
      }

      // VERIFY UPDATE WAS SAVED
      const { data: verify } = await supabase.from('products').select('*').eq('id', editingProduct.id).single();
      if (!verify) {
        showToast('Update verification failed - please try again', 'error');
        return;
      }

      setEditingProduct(null);
      setShowModal(false);
      showToast(`Updated ${editingProduct.name}`, 'success');

      // FORCE REFRESH TO ENSURE SYNC
      setCachedProducts(new Map());
      fetchProducts(currentPage, searchTerm, selectedCategory, true);

    } catch (error) {
      console.error('Error updating product:', error);
      showToast('Error updating product: ' + error.message, 'error');
    }
  };

  const deleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        // INSTANT UI UPDATE
        const productToDelete = products.find(p => p.id === id);
        setProducts(prev => prev.filter(p => p.id !== id));
        showToast(`Deleted ${productToDelete?.name}`, 'success');

        // IMMEDIATE DATABASE DELETE
        const { error } = await supabase
          .from('products')
          .delete()
          .eq('id', id)
          .eq('user_id', user.id);

        if (error) {
          setProducts(prev => [...prev, productToDelete].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
          showToast('Error deleting product: ' + error.message, 'error');
        }
      } catch (error) {
        console.error('Error deleting product:', error);
        showToast('Error deleting product: ' + error.message, 'error');
      }
    }
  };

  // Check if product can be edited (within 10 minutes of creation)
  const canEditProduct = (product) => {
    const createdAt = new Date(product.created_at);
    const now = new Date();
    const timeDiff = (now - createdAt) / (1000 * 60); // difference in minutes
    return timeDiff <= 10;
  };

  const editProduct = (product) => {
    if (!canEditProduct(product)) {
      showToast('Cannot edit item after 10 minutes of creation', 'error');
      return;
    }
    setEditingProduct({ ...product });
    setShowModal(true);
  };

  const addCategory = async () => {
    if (!newCategory.trim()) {
      showToast('Please enter a category name', 'error');
      return;
    }

    if (categories.includes(newCategory.trim())) {
      showToast('Category already exists', 'error');
      return;
    }

    try {
      const categoryName = newCategory.trim();

      // ⚡ INSTANT UI UPDATE (optimistic)
      setCategories(prev => [...prev, categoryName].sort());
      setNewCategory('');
      setShowCategoryModal(false);
      showToast(`Added category: ${categoryName}`, 'success');

      // Database insert in background
      supabase
        .from('categories')
        .insert([{ name: categoryName, user_id: user.id }])
        .then(({ error }) => {
          if (error) {
            // Revert on error
            setCategories(prev => prev.filter(cat => cat !== categoryName));
            showToast('Error adding category: ' + error.message, 'error');
          }
        });
    } catch (error) {
      console.error('Error adding category:', error);
      showToast('Error adding category: ' + error.message, 'error');
    }
  };

  const deleteCategory = async (categoryToDelete) => {
    if (window.confirm(`Are you sure you want to delete the category "${categoryToDelete}"? All products in this category will be moved to "Uncategorized".`)) {
      try {
        // INSTANT UI UPDATES
        const originalCategories = [...categories];
        const originalProducts = [...products];

        // Update UI immediately
        if (!categories.includes('Uncategorized')) {
          setCategories(prev => [...prev, 'Uncategorized'].sort());
        }
        setCategories(prev => prev.filter(cat => cat !== categoryToDelete));
        setProducts(prev => prev.map(p =>
          p.category === categoryToDelete ? { ...p, category: 'Uncategorized', updated_at: new Date().toISOString() } : p
        ));
        showToast(`Deleted category: ${categoryToDelete}`, 'success');

        // IMMEDIATE DATABASE OPERATIONS
        // Add Uncategorized if it doesn't exist
        if (!originalCategories.includes('Uncategorized')) {
          await supabase.from('categories').insert([{ name: 'Uncategorized', user_id: user.id }]);
        }

        // Update all products in this category to Uncategorized
        const updateResult = await supabase
          .from('products')
          .update({ category: 'Uncategorized', updated_at: new Date().toISOString() })
          .eq('category', categoryToDelete)
          .eq('user_id', user.id);

        // Delete the category
        const deleteResult = await supabase
          .from('categories')
          .delete()
          .eq('name', categoryToDelete)
          .eq('user_id', user.id);

        if (updateResult.error || deleteResult.error) {
          // Revert UI changes on error
          setCategories(originalCategories);
          setProducts(originalProducts);
          showToast('Error deleting category: ' + (updateResult.error?.message || deleteResult.error?.message), 'error');
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        showToast('Error deleting category: ' + error.message, 'error');
      }
    }
  };



  // COMPLETE DRAG AND DROP SYSTEM
  const handleDragStart = (e, productId) => {
    e.dataTransfer.setData('productId', productId.toString());
    setDraggedProduct(productId);
    e.currentTarget.style.opacity = '0.7';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedProduct(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drop-zone-highlight');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drop-zone-highlight');
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drop-zone-highlight');
    const productId = e.dataTransfer.getData('productId');
    if (!productId) return;

    // Block moving from pending status - only approval app can approve
    const product = products.find(p => p.id === parseInt(productId));
    if (product && product.status === 'pending') {
      showToast('Use the approval app to approve pending items', 'error');
      return;
    }

    // Instant update without await
    updateProductStatus(parseInt(productId), newStatus);
  };

  // MOBILE-FIRST TOUCH SUPPORT
  const handleTouchStart = (e, productId) => {
    const startTime = Date.now();
    setDragStartTime(startTime);
    setDraggedProduct(productId);
    setTouchStartY(e.touches[0].clientY);

    // Delay drag start to avoid conflicts with scrolling
    setTimeout(() => {
      if (draggedProduct === productId && Date.now() - startTime > 150) {
        setIsDragging(true);
        e.currentTarget.style.opacity = '0.7';
        e.currentTarget.style.transform = 'scale(1.05)';
        e.currentTarget.style.zIndex = '1000';
        e.currentTarget.style.transition = 'all 0.2s ease';

        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }
    }, 150);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !draggedProduct) return;
    e.preventDefault();

    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);

    document.querySelectorAll('.drop-zone-highlight').forEach(el => {
      el.classList.remove('drop-zone-highlight');
    });

    const dropZone = element?.closest('[data-drop-zone]');
    if (dropZone) {
      dropZone.classList.add('drop-zone-highlight');
      if (navigator.vibrate) {
        navigator.vibrate(25);
      }
    }
  };

  const handleTouchEnd = async (e) => {
    const touchDuration = Date.now() - dragStartTime;

    if (!isDragging || touchDuration < 150) {
      // Short tap - reset and return
      setDraggedProduct(null);
      setIsDragging(false);
      return;
    }

    if (!draggedProduct) return;

    const touch = e.changedTouches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);

    e.currentTarget.style.opacity = '1';
    e.currentTarget.style.transform = 'scale(1)';
    e.currentTarget.style.zIndex = 'auto';
    e.currentTarget.style.transition = 'all 0.2s ease';

    document.querySelectorAll('.drop-zone-highlight').forEach(el => {
      el.classList.remove('drop-zone-highlight');
    });

    const dropZone = element?.closest('[data-drop-zone]');
    if (dropZone && draggedProduct) {
      const newStatus = dropZone.getAttribute('data-drop-zone');



      // Block moving from pending status - only approval app can approve
      const product = products.find(p => p.id === draggedProduct);
      if (product && product.status === 'pending') {
        showToast('Use the approval app to approve pending items', 'error');
        setDraggedProduct(null);
        setIsDragging(false);
        return;
      }

      updateProductStatus(draggedProduct, newStatus);

      if (navigator.vibrate) {
        navigator.vibrate([50, 50, 50]);
      }
    }

    setDraggedProduct(null);
    setIsDragging(false);
  };

  const updateProductStatus = async (productId, newStatus) => {
    try {
      const product = products.find(p => p.id === productId);
      if (!product) return;

      // Handle sell functionality
      if (newStatus === 'sell') {
        await handleSell(productId);
        return;
      }

      const finalStatus = newStatus;
      const updatedAt = new Date().toISOString();

      // ⚡ INSTANT UI UPDATE (optimistic)
      setProducts(prevProducts =>
        prevProducts.map(p =>
          p.id === productId ? { ...p, status: finalStatus, updated_at: updatedAt } : p
        )
      );

      showToast(`${product.name} moved to ${finalStatus.replace('-', ' ')}`, 'success');

      // Database update in background
      supabase
        .from('products')
        .update({
          status: finalStatus,
          updated_at: updatedAt
        })
        .eq('id', productId)
        .eq('user_id', user.id)
        .then(({ error }) => {
          if (error) {
            // Revert on error
            setProducts(prevProducts =>
              prevProducts.map(p =>
                p.id === productId ? { ...p, status: product.status } : p
              )
            );
            showToast('Error updating product status: ' + error.message, 'error');
          }
        });

    } catch (error) {
      console.error('Error updating product status:', error);
      showToast('Error updating product status: ' + error.message, 'error');
    }
  };

  const handleSell = async (productId, quantity = 1) => {
    const product = products.find(p => p.id === productId);
    const inputQty = sellQuantity[productId];
    const sellQty = inputQty && inputQty !== '' && inputQty !== 0 ? parseInt(inputQty) : 1;

    if (!product || isNaN(sellQty) || sellQty <= 0 || sellQty > product.stock) {
      showToast('Invalid quantity or insufficient stock', 'error');
      return;
    }

    try {
      // Use the atomic sale processing function to prevent race conditions
      const { data, error } = await supabase.rpc('process_sale_immediate', {
        p_product_id: productId,
        p_quantity: sellQty,
        p_user_id: user.id
      });

      if (error) {
        showToast('Error processing sale: ' + error.message, 'error');
        return;
      }

      const result = data;
      if (!result.success) {
        showToast('Sale failed: ' + result.error, 'error');
        return;
      }

      const totalAmount = sellQty * product.price;
      setSellQuantity(prev => ({ ...prev, [productId]: 1 }));
      showToast(`Sold ${sellQty} ${product.name}(s) for ₹${totalAmount.toLocaleString('en-IN')}`);

      // FORCE REFRESH ALL DATA
      setCachedProducts(new Map());
      fetchProducts(currentPage, searchTerm, selectedCategory, true);
      fetchSales();

    } catch (error) {
      console.error('Error processing sale:', error);
      showToast('Error processing sale: ' + error.message, 'error');
    }
  };

  // UTILITY FUNCTIONS
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getStatusProducts = (status) => {
    return filteredProducts.filter(product => product.status === status);
  };

  const getTotalValue = () => {
    return products.reduce((total, product) => total + (product.price * product.stock), 0).toLocaleString('en-IN');
  };

  const getTotalItems = () => {
    return products.reduce((total, product) => total + product.stock, 0);
  };

  const toggleMonth = (monthKey) => {
    const newExpanded = new Set(expandedMonths);
    if (newExpanded.has(monthKey)) {
      newExpanded.delete(monthKey);
    } else {
      newExpanded.add(monthKey);
    }
    setExpandedMonths(newExpanded);
  };

  const getMonthName = (monthKey) => {
    const date = new Date(monthKey + '-01');
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 gradient-smooth gpu-accelerated">
      <AppOnboarding />
      {/* Toast Notifications - FIXED */}
      <div className="fixed top-4 right-4 z-[9999] space-y-2 max-w-sm">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`toast ${toast.type} show bg-white shadow-lg rounded-lg border-l-4 p-4 transform transition-all duration-300 ease-in-out`}
            style={{
              borderLeftColor: toast.type === 'success' ? '#10b981' :
                toast.type === 'error' ? '#ef4444' :
                  toast.type === 'warning' ? '#f59e0b' : '#3b82f6'
            }}
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-800 text-sm">{toast.message}</span>
              <button
                className="ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1"
                onClick={(e) => {
                  e.stopPropagation();
                  removeToast(toast.id);
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile-Optimized Header */}
      <header className="bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-xl gradient-smooth">
        <div className="container mx-auto px-3 md:px-6 py-3 md:py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between header-mobile">
            <div className="mb-4 md:mb-0 text-center md:text-left">
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                <span className="text-yellow-300">Billify</span> Inventory
              </h1>
              <p className="text-amber-100 text-sm md:text-base">Complete Inventory Management Solution</p>
            </div>
            <div className="flex flex-col lg:flex-row gap-3 md:gap-4 items-center">
              <div className="flex flex-col md:flex-row flex-wrap gap-3 items-center w-full md:w-auto">
                <div className="relative w-full md:w-auto">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-3 rounded-xl border-0 focus:ring-3 focus:ring-amber-300 w-full md:w-64 text-gray-800 font-medium"
                  />
                  <svg className="w-5 h-5 absolute left-3 top-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <div className="relative group">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="appearance-none px-4 py-3 pr-12 rounded-xl border-0 focus:ring-3 focus:ring-amber-300 bg-white text-gray-800 font-medium cursor-pointer hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md min-w-[180px] group-hover:shadow-lg"
                  >
                    <option value="all">📂 All Categories ({products.length})</option>
                    {categories.map(category => {
                      const categoryCount = products.filter(p => p.category === category).length;
                      return (
                        <option key={category} value={category}>📁 {category} ({categoryCount})</option>
                      );
                    })}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {/* Category count badge */}
                  {selectedCategory !== 'all' && (
                    <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-lg">
                      {products.filter(p => p.category === selectedCategory).length}
                    </div>
                  )}
                </div>
                {/* Clear Filter Button */}
                {selectedCategory !== 'all' && (
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className="px-3 py-3 bg-gray-100 text-gray-600 rounded-xl font-medium hover:bg-gray-200 transition-all duration-200 flex items-center justify-center text-sm min-h-[48px]"
                    title="Clear category filter"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Clear
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 w-full md:w-auto justify-center md:justify-start mobile-buttons">
                <button
                  onClick={() => setShowCategoryModal(true)}
                  className="bg-white text-amber-700 px-4 py-3 rounded-xl font-semibold hover:bg-amber-50 transition-colors flex items-center justify-center text-base min-h-[48px] flex-1 sm:flex-initial"
                  style={{ touchAction: 'manipulation' }}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Category
                </button>
                <button
                  onClick={() => {
                    setEditingProduct(null);
                    setShowModal(true);
                  }}
                  className="bg-white text-amber-700 px-4 py-3 rounded-xl font-semibold hover:bg-amber-50 transition-colors flex items-center justify-center text-base min-h-[48px] flex-1 sm:flex-initial"
                  style={{ touchAction: 'manipulation' }}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Product
                </button>
                <button
                  onClick={() => setShowSalesHistory(true)}
                  className="bg-white text-green-700 px-4 py-3 rounded-xl font-semibold hover:bg-green-50 transition-colors flex items-center justify-center text-base min-h-[48px] flex-1 sm:flex-initial"
                  style={{ touchAction: 'manipulation' }}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Sales
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Logout button clicked');
                    handleLogout();
                  }}
                  disabled={loading}
                  className="bg-red-500 text-white px-4 py-3 rounded-xl font-semibold hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-base min-h-[48px] w-full sm:w-auto"
                  style={{ touchAction: 'manipulation' }}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {loading ? 'Logging out...' : 'Logout'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="container mx-auto px-4 md:px-6 py-3 md:py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-4 md:mb-6">
          <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8 border-t-4 border-amber-500 card-smooth stagger-item gpu-accelerated">
            <div className="flex items-center">
              <div className="p-2 md:p-4 rounded-2xl bg-amber-100 text-amber-600 mr-3 md:mr-6">
                <svg className="w-6 h-6 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-gray-500 text-xs md:text-sm font-medium">Total Inventory Value</p>
                <p className="text-xl md:text-3xl font-bold text-gray-800 mt-1">₹{getTotalValue()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8 border-t-4 border-green-500 card-smooth stagger-item gpu-accelerated">
            <div className="flex items-center">
              <div className="p-2 md:p-4 rounded-2xl bg-green-100 text-green-600 mr-3 md:mr-6">
                <svg className="w-6 h-6 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <div>
                <p className="text-gray-500 text-xs md:text-sm font-medium">Total Items in Stock</p>
                <p className="text-xl md:text-3xl font-bold text-gray-800 mt-1">{getTotalItems()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8 border-t-4 border-blue-500 card-smooth stagger-item gpu-accelerated">
            <div className="flex items-center">
              <div className="p-2 md:p-4 rounded-2xl bg-blue-100 text-blue-600 mr-3 md:mr-6">
                <svg className="w-6 h-6 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <p className="text-gray-500 text-xs md:text-sm font-medium">Total Products</p>
                <p className="text-xl md:text-3xl font-bold text-gray-800 mt-1">{products.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search Loading Indicator - Only show during user searches */}
        {searchLoading && (
          <div className="flex justify-center items-center py-4">
            <div className="loading-spin rounded-full h-6 w-6 border-b-2 border-amber-600 mr-2"></div>
            <span className="text-gray-600">Searching...</span>
          </div>
        )}

        {/* Pagination Info */}
        <div className="flex justify-between items-center mb-4 text-sm text-gray-600">
          <span>Showing {products.length} of {totalProducts} products</span>
          <span>Page {currentPage}</span>
        </div>

        {/* Mobile-Optimized Kanban Board */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6 items-start">
          {/* Pending Column - READ ONLY */}
          <div className="bg-gray-100 rounded-2xl p-3 lg:p-6 h-fit kanban-mobile">
            <div className="bg-yellow-500 text-white p-3 lg:p-4 rounded-xl flex items-center justify-between mb-3 lg:mb-6">
              <h2 className="font-bold text-lg lg:text-xl">Pending Approval</h2>
              <span className="bg-white text-yellow-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                {getStatusProducts('pending').length}
              </span>
            </div>
            <div className="bg-orange-100 border border-orange-300 rounded-lg p-2 mb-4">
              <p className="text-orange-700 text-xs font-medium text-center">
                ⚠️ Use approval app to approve these items
              </p>
            </div>
            <div className="space-y-4">
              {getStatusProducts('pending').map(product => (
                <div
                  key={product.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, product.id)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => handleTouchStart(e, product.id)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onClick={() => showPendingProductDetails(product)}
                  className="bg-white rounded-xl p-3 lg:p-4 shadow-lg border-l-4 border-yellow-500 cursor-pointer hover:shadow-xl transition-all duration-200 touch-manipulation select-none min-h-[120px] sm:min-h-[100px]"
                  style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none', WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-800 text-lg">{product.name}</h3>
                    <button onClick={() => deleteProduct(product.id)} className="text-red-500 hover:text-red-700 p-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>

                  {/* Product ID */}
                  <div className="mb-2">
                    <span className="text-xs text-gray-500 font-mono">ID: {product.id}</span>
                  </div>

                  {/* Category */}
                  <div className="mb-2">
                    <span className="inline-flex items-center bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 text-sm px-4 py-2 rounded-full font-medium shadow-sm hover:shadow-md transition-all duration-200">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      {product.category}
                    </span>
                  </div>

                  {/* Description */}
                  {product.description && (
                    <p className="text-sm text-gray-600 mb-3 leading-relaxed">
                      {product.description}
                    </p>
                  )}

                  {/* Price and Stock Details */}
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <p className="font-bold text-gray-800 text-lg">₹{product.price.toLocaleString('en-IN')}</p>
                      <p className="text-sm text-blue-600 font-medium">Stock: {product.stock} units</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Total Value</p>
                      <p className="font-bold text-green-600">₹{(product.price * product.stock).toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  {/* Status and Created Date */}
                  <div className="flex justify-between items-center">
                    <span className="text-orange-600 text-sm bg-orange-50 px-3 py-2 rounded-full font-medium flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Awaiting Approval
                    </span>
                    <div className="text-right">
                      <span className="text-xs text-gray-500 block">
                        {new Date(product.created_at).toLocaleDateString('en-IN')}
                      </span>
                      <span className="text-xs text-blue-600 font-medium">
                        Click for details
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {getStatusProducts('pending').length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No pending products</p>
                </div>
              )}
            </div>
          </div>

          {/* In Stock Column */}
          <div
            className="bg-gray-100 rounded-2xl p-6 h-fit"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, 'in-stock')}
            data-drop-zone="in-stock"
          >
            <div className="bg-green-500 text-white p-4 rounded-xl flex items-center justify-between mb-6">
              <h2 className="font-bold text-xl">In Stock</h2>
              <span className="bg-white text-green-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                {getStatusProducts('in-stock').length}
              </span>
            </div>
            <div className="space-y-4">
              {getStatusProducts('in-stock').map(product => (
                <div
                  key={product.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, product.id)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => handleTouchStart(e, product.id)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  className="bg-white rounded-xl p-3 sm:p-4 shadow-lg border-l-4 border-green-500 cursor-grab active:cursor-grabbing transition-all duration-200 min-h-[120px] sm:min-h-[100px]"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-800">{product.name}</h3>
                    <button onClick={() => deleteProduct(product.id)} className="text-red-500 hover:text-red-700">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  <div className="mb-2">
                    <span className="inline-flex items-center bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 text-sm px-4 py-2 rounded-full font-medium shadow-sm hover:shadow-md transition-all duration-200">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      {product.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{product.description}</p>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-gray-800">₹{product.price.toLocaleString('en-IN')}</p>
                      <p className="text-sm text-green-600">Stock: {product.stock}</p>
                    </div>
                    {canEditProduct(product) ? (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          editProduct(product);
                        }}
                        className="text-blue-600 text-sm bg-blue-50 px-3 py-2 rounded min-h-[44px] min-w-[60px] flex items-center justify-center touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        Edit
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs bg-gray-100 px-3 py-2 rounded min-h-[44px] min-w-[60px] flex items-center justify-center">
                        Locked
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {getStatusProducts('in-stock').length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No products in stock</p>
                </div>
              )}
            </div>
          </div>

          {/* Sell Items Column */}
          <div
            className="bg-gray-100 rounded-2xl p-6 h-fit"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, 'sell')}
            data-drop-zone="sell"
          >
            <div className="bg-blue-500 text-white p-4 rounded-xl flex items-center justify-between mb-6">
              <h2 className="font-bold text-xl">Sell Items</h2>
              <span className="bg-white text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                {products.filter(p => p.stock > 0 && p.status !== 'pending').length}
              </span>
            </div>
            <div className="space-y-4">
              {products.filter(p => p.stock > 0 && p.status !== 'pending').map(product => (
                <div
                  key={product.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, product.id)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => handleTouchStart(e, product.id)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  className="bg-white rounded-xl p-3 sm:p-4 shadow-lg border-l-4 border-blue-500 cursor-grab active:cursor-grabbing transition-all duration-200 touch-manipulation select-none min-h-[120px] sm:min-h-[100px]"
                  style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none', WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-800">{product.name}</h3>
                    <button onClick={() => deleteProduct(product.id)} className="text-red-500 hover:text-red-700">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  <div className="mb-2">
                    <span className="inline-flex items-center bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 text-sm px-4 py-2 rounded-full font-medium shadow-sm hover:shadow-md transition-all duration-200">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      {product.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{product.description}</p>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-gray-800">₹{product.price.toLocaleString('en-IN')}</p>
                      <p className="text-sm text-blue-600">Stock: {product.stock}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-2">
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-gray-600">Qty:</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const current = sellQuantity[product.id] || 1;
                            if (current > 1) {
                              setSellQuantity(prev => ({ ...prev, [product.id]: current - 1 }));
                            }
                          }}
                          className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center justify-center text-lg font-bold"
                        >
                          -
                        </button>
                        <span className="w-12 text-center font-bold text-lg">{sellQuantity[product.id] || 1}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const current = sellQuantity[product.id] || 1;
                            if (current < product.stock) {
                              setSellQuantity(prev => ({ ...prev, [product.id]: current + 1 }));
                            }
                          }}
                          className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center justify-center text-lg font-bold"
                        >
                          +
                        </button>
                      </div>
                      <button
                        onPointerDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const qty = sellQuantity[product.id] || 1;
                          handleSell(product.id, qty);
                        }}
                        className="bg-green-500 hover:bg-green-600 active:bg-green-700 text-white px-4 py-3 rounded-lg text-base font-semibold min-h-[48px] flex-1 sm:flex-initial sm:min-w-[80px] flex items-center justify-center transition-colors"
                        style={{
                          WebkitTapHighlightColor: 'rgba(34, 197, 94, 0.3)',
                          touchAction: 'manipulation',
                          userSelect: 'none'
                        }}
                      >
                        Sell
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {products.filter(p => p.stock > 0 && p.status !== 'pending').length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No items available for sale</p>
                </div>
              )}
            </div>
          </div>

          {/* Low Stock & Out of Stock Column */}
          <div className="space-y-6 h-fit">
            {/* Low Stock */}
            <div
              className="bg-gray-100 rounded-2xl p-6 h-fit"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'low-stock')}
              data-drop-zone="low-stock"
            >
              <div className="bg-orange-500 text-white p-4 rounded-xl flex items-center justify-between mb-6">
                <h2 className="font-bold text-lg">Low Stock</h2>
                <span className="bg-white text-orange-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                  {getStatusProducts('low-stock').length}
                </span>
              </div>
              <div className="space-y-4">
                {getStatusProducts('low-stock').map(product => (
                  <div
                    key={product.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, product.id)}
                    onDragEnd={handleDragEnd}
                    onTouchStart={(e) => handleTouchStart(e, product.id)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    className="bg-white rounded-xl p-3 shadow-lg border-l-4 border-orange-500 cursor-grab active:cursor-grabbing transition-all duration-200 min-h-[100px]"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-bold text-gray-800 text-sm">{product.name}</h3>
                      <button onClick={() => deleteProduct(product.id)} className="text-red-500 hover:text-red-700">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold text-gray-800 text-sm">₹{product.price.toLocaleString('en-IN')}</p>
                        <p className="text-xs text-orange-600">Stock: {product.stock}</p>
                      </div>
                      {canEditProduct(product) ? (
                        <button onClick={() => editProduct(product)} className="text-blue-600 text-xs bg-blue-50 px-2 py-1 rounded">Edit</button>
                      ) : (
                        <span className="text-gray-400 text-xs bg-gray-100 px-2 py-1 rounded">Locked</span>
                      )}
                    </div>
                  </div>
                ))}
                {getStatusProducts('low-stock').length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    <p>No low stock products</p>
                  </div>
                )}
              </div>
            </div>

            {/* Out of Stock */}
            <div
              className="bg-gray-100 rounded-2xl p-6 h-fit"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'out-of-stock')}
              data-drop-zone="out-of-stock"
            >
              <div className="bg-red-500 text-white p-4 rounded-xl flex items-center justify-between mb-6">
                <h2 className="font-bold text-lg">Out of Stock</h2>
                <span className="bg-white text-red-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                  {getStatusProducts('out-of-stock').length}
                </span>
              </div>
              <div className="space-y-4">
                {getStatusProducts('out-of-stock').map(product => (
                  <div
                    key={product.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, product.id)}
                    onDragEnd={handleDragEnd}
                    onTouchStart={(e) => handleTouchStart(e, product.id)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    className="bg-white rounded-xl p-3 shadow-lg border-l-4 border-red-500 cursor-grab active:cursor-grabbing transition-all duration-200 min-h-[100px]"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-bold text-gray-800 text-sm">{product.name}</h3>
                      <button onClick={() => deleteProduct(product.id)} className="text-red-500 hover:text-red-700">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold text-gray-800 text-sm">₹{product.price.toLocaleString('en-IN')}</p>
                        <p className="text-xs text-red-600">Stock: {product.stock}</p>
                      </div>
                      {canEditProduct(product) ? (
                        <button onClick={() => editProduct(product)} className="text-blue-600 text-xs bg-blue-50 px-2 py-1 rounded">Edit</button>
                      ) : (
                        <span className="text-gray-400 text-xs bg-gray-100 px-2 py-1 rounded">Locked</span>
                      )}
                    </div>
                  </div>
                ))}
                {getStatusProducts('out-of-stock').length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    <p>No out of stock products</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-center items-center gap-4 mt-8">
          <button
            onClick={() => {
              if (currentPage > 1) {
                const newPage = currentPage - 1;
                setCurrentPage(newPage);
                fetchProducts(newPage, searchTerm, selectedCategory, false, true);
              }
            }}
            disabled={currentPage === 1 || searchLoading}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-700 transition-colors"
          >
            Previous
          </button>

          <span className="px-4 py-2 bg-gray-100 rounded-lg font-medium">
            Page {currentPage}
          </span>

          <button
            onClick={() => {
              if (hasMore) {
                const newPage = currentPage + 1;
                setCurrentPage(newPage);
                fetchProducts(newPage, searchTerm, selectedCategory, false, true);
              }
            }}
            disabled={!hasMore || searchLoading}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-700 transition-colors"
          >
            Next
          </button>
        </div>
      </div>

      {/* Modal for Add/Edit Product */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-2 sm:p-4 z-[9999] viewport-centered" style={{ backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}>
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[85vh] overflow-hidden transform transition-all duration-300 ease-out mx-2 sm:mx-0">
            {/* Professional Header */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <div className="flex justify-between items-start sm:items-center">
                <div className="flex-1 pr-2">
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-800 flex items-center">
                    {editingProduct ? (
                      <>
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit Product
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add New Product
                      </>
                    )}
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600 mt-1 hidden sm:block">
                    {editingProduct ? 'Update product information' : 'Fill in the details to add a new product'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingProduct(null);
                  }}
                  className="text-gray-600 hover:text-gray-800 p-2 rounded-full hover:bg-white transition-all duration-200 bg-white shadow-sm border border-gray-200 flex-shrink-0"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Compact Form Content */}
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[70vh] sm:max-h-[60vh]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {/* Product Name */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={editingProduct ? editingProduct.name : newProduct.name}
                    onChange={(e) => editingProduct
                      ? setEditingProduct({ ...editingProduct, name: e.target.value })
                      : setNewProduct({ ...newProduct, name: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-800 transition-all duration-200"
                    placeholder="Enter product name"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    value={editingProduct ? editingProduct.category : newProduct.category}
                    onChange={(e) => editingProduct
                      ? setEditingProduct({ ...editingProduct, category: e.target.value })
                      : setNewProduct({ ...newProduct, category: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-800 transition-all duration-200"
                  >
                    <option value="">Select category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Price (₹) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editingProduct ? editingProduct.price : newProduct.price}
                    onChange={(e) => editingProduct
                      ? setEditingProduct({ ...editingProduct, price: e.target.value })
                      : setNewProduct({ ...newProduct, price: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-800 transition-all duration-200"
                    placeholder="0.00"
                  />
                </div>

                {/* Stock */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Stock Quantity *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editingProduct ? editingProduct.stock : newProduct.stock}
                    onChange={(e) => editingProduct
                      ? setEditingProduct({ ...editingProduct, stock: e.target.value })
                      : setNewProduct({ ...newProduct, stock: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-800 transition-all duration-200"
                    placeholder="0"
                  />
                </div>

                {/* Description */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={editingProduct ? editingProduct.description : newProduct.description}
                    onChange={(e) => editingProduct
                      ? setEditingProduct({ ...editingProduct, description: e.target.value })
                      : setNewProduct({ ...newProduct, description: e.target.value })
                    }
                    rows="2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-800 transition-all duration-200 resize-none"
                    placeholder="Enter product description"
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Professional Footer */}
            <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingProduct(null);
                  }}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={editingProduct ? updateProduct : addProduct}
                  className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                >
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Add Category */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-2 sm:p-4 z-[9999] viewport-centered" style={{ backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}>
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden mx-2 sm:mx-0">
            {/* Professional Header */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <div className="flex justify-between items-start sm:items-center">
                <div className="flex-1 pr-2">
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-800 flex items-center">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <span className="truncate">Manage Categories</span>
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600 mt-1 hidden sm:block">Add, edit, or remove product categories</p>
                </div>
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="text-gray-600 hover:text-gray-800 p-2 rounded-full hover:bg-white transition-all duration-200 bg-white shadow-sm border border-gray-200 flex-shrink-0"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto max-h-[75vh] sm:max-h-[70vh]">
              {/* Add New Category Section */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add New Category
                </h3>
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-800 transition-all duration-200 text-base"
                    placeholder="Enter category name"
                  />
                  <button
                    onClick={addCategory}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                  >
                    <span className="flex items-center justify-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add
                    </span>
                  </button>
                </div>
              </div>

              {/* Existing Categories Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Existing Categories
                </h3>
                <div className="space-y-2 sm:space-y-3 max-h-60 sm:max-h-80 overflow-y-auto pr-1 sm:pr-2 category-scroll">
                  {categories.map(category => (
                    <div key={category} className="flex justify-between items-center p-3 sm:p-4 bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
                      <div className="flex flex-col sm:flex-row sm:items-center flex-1 min-w-0">
                        <span className="inline-flex items-center bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-1.5 rounded-full font-medium shadow-sm mb-1 sm:mb-0 sm:mr-3 w-fit">
                          <svg className="w-3 h-3 mr-1 sm:mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          <span className="truncate">{category}</span>
                        </span>
                        <span className="text-xs sm:text-sm text-gray-500">
                          {products.filter(p => p.category === category).length} products
                        </span>
                      </div>
                      <button
                        onClick={() => deleteCategory(category)}
                        className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-all duration-200 flex-shrink-0 ml-2"
                        title="Delete category"
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  {categories.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      <p>No categories yet. Add your first category above!</p>
                    </div>
                  )}
                  {categories.length > 0 && (
                    <div className="text-center py-2 text-xs text-gray-400 border-t border-gray-200 mt-3">
                      <p>Scroll to see all {categories.length} categories</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Sales History Modal */}
      {showSalesHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-2 sm:p-4 z-[9999] viewport-centered" style={{ backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}>
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-6xl w-full max-h-[95vh] sm:max-h-[85vh] overflow-hidden mx-2 sm:mx-0">
            <div className="flex justify-between items-start sm:items-center p-4 sm:p-6 border-b border-gray-200">
              <div className="flex-1 pr-2">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Sales History</h2>
                <p className="text-sm sm:text-base text-gray-600 mt-1 hidden sm:block">Complete record of all your sales transactions</p>
              </div>
              <button
                onClick={() => setShowSalesHistory(false)}
                className="text-gray-600 hover:text-gray-800 p-2 rounded-full hover:bg-white transition-all duration-200 bg-white shadow-sm border border-gray-200 flex-shrink-0"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto max-h-[75vh] sm:max-h-[70vh]">
              {/* Sales Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg mr-3">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-green-600 font-medium">Total Revenue</p>
                      <p className="text-xl font-bold text-green-800">
                        ₹{sales.reduce((total, sale) => total + parseFloat(sale.total_amount), 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg mr-3">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Total Sales</p>
                      <p className="text-xl font-bold text-blue-800">{sales.length}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100 rounded-lg mr-3">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Items Sold</p>
                      <p className="text-xl font-bold text-purple-800">
                        {sales.reduce((total, sale) => total + sale.quantity_sold, 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales by Month */}
              <div className="max-h-96 overflow-y-auto">
                {Object.keys(monthlySales).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <p>No sales recorded yet</p>
                    <p className="text-sm text-gray-400 mt-1">Start selling products to see your sales history here</p>
                  </div>
                ) : (
                  Object.keys(monthlySales).sort().reverse().map(monthKey => {
                    const monthSales = monthlySales[monthKey];
                    const isCurrentMonth = monthKey === getCurrentMonth();
                    const isExpanded = isCurrentMonth || expandedMonths.has(monthKey);

                    return (
                      <div key={monthKey} className="mb-6">
                        {/* Month Header */}
                        <div
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => toggleMonth(monthKey)}
                        >
                          <div className="flex items-center">
                            <svg className={`w-4 h-4 mr-2 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                            <h3 className="text-lg font-semibold text-gray-800">{getMonthName(monthKey)}</h3>
                          </div>
                          <span className="text-sm text-gray-600">{monthSales.length} sales</span>
                        </div>

                        {/* Sales Table */}
                        {isExpanded && (
                          <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {monthSales.map((sale, index) => (
                                  <tr key={sale.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                      <div>
                                        <p className="font-medium">{new Date(sale.created_at).toLocaleDateString('en-IN')}</p>
                                        <p className="text-gray-500 text-xs">{new Date(sale.created_at).toLocaleTimeString('en-IN')}</p>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 text-center">{sale.product_name}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {sale.quantity_sold}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 text-center">₹{parseFloat(sale.sale_price).toLocaleString('en-IN')}</td>
                                    <td className="px-4 py-3 text-sm font-semibold text-green-600 text-center">₹{parseFloat(sale.total_amount).toLocaleString('en-IN')}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900 text-center">
                                      <button
                                        onClick={() => undoSale(sale.id)}
                                        className="inline-flex items-center px-3 py-1.5 border border-red-300 text-red-700 text-xs font-medium rounded-md hover:bg-red-50 hover:border-red-400 transition-colors"
                                      >
                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                        </svg>
                                        Undo
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowSalesHistory(false)}
                className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Product Details Modal */}
      {showPendingDetails && selectedPendingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-2 sm:p-4 z-[9999] viewport-centered" style={{ backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}>
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden mx-2 sm:mx-0">
            <div className="flex justify-between items-start sm:items-center p-4 sm:p-6 border-b border-gray-200">
              <div className="flex-1 pr-2">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Product Details</h2>
                <p className="text-sm sm:text-base text-gray-600 mt-1 hidden sm:block">Complete information about this pending product</p>
              </div>
              <button
                onClick={() => setShowPendingDetails(false)}
                className="text-gray-600 hover:text-gray-800 p-2 rounded-full hover:bg-white transition-all duration-200 bg-white shadow-sm border border-gray-200 flex-shrink-0"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto max-h-[75vh] sm:max-h-[70vh]">
              {/* Product Header */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-gray-800">{selectedPendingProduct.name}</h3>
                  <span className="inline-flex items-center bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-medium shadow-sm">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    {selectedPendingProduct.category}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-mono">ID: {selectedPendingProduct.id}</span>
                  <span className="mx-2">•</span>
                  <span>Created: {new Date(selectedPendingProduct.created_at).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                </div>
              </div>

              {/* Product Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                {/* Price Information */}
                <div className="bg-green-50 rounded-xl p-4">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                    Price Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Unit Price:</span>
                      <span className="font-bold text-green-600">₹{selectedPendingProduct.price.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Stock Quantity:</span>
                      <span className="font-bold text-blue-600">{selectedPendingProduct.stock} units</span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600 font-medium">Total Value:</span>
                        <span className="font-bold text-green-700 text-lg">₹{(selectedPendingProduct.price * selectedPendingProduct.stock).toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Information */}
                <div className="bg-orange-50 rounded-xl p-4">
                  <h4 className="font-semibold text-orange-800 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Status Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Current Status:</span>
                      <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-sm font-medium">
                        Awaiting Approval
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Action Required:</span>
                      <span className="text-orange-600 text-sm">Use Approval App</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Can Edit:</span>
                      <span className="text-gray-600 text-sm">
                        {canEditProduct(selectedPendingProduct) ? 'Yes' : 'No (Locked)'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedPendingProduct.description && (
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Description
                  </h4>
                  <p className="text-gray-700 leading-relaxed">{selectedPendingProduct.description}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <div className="flex items-center mb-3">
                  <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <h4 className="font-semibold text-yellow-800">Next Steps</h4>
                </div>
                <p className="text-yellow-700 text-sm mb-3">
                  This product is waiting for approval. Use the <strong>Approval System</strong> to review and approve this product before it can be moved to "In Stock" status.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setShowPendingDetails(false);
                      showToast('Opening approval system...', 'info');
                    }}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium"
                  >
                    Open Approval System
                  </button>
                  <button
                    onClick={() => deleteProduct(selectedPendingProduct.id)}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                  >
                    Delete Product
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowPendingDetails(false)}
                className="px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-16">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-300">© 2025 भारत Inventory Pro. Made with ❤️ for Indian Businesses.</p>
          <p className="text-amber-300 mt-2 font-medium">
            Developed by
            <a
              href="https://subhojit-paul.pages.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-yellow-300 transition-colors cursor-pointer underline"
            >
              Subhojit
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;